// @flow

import Icon from '../Icon';
import { createKeyHandler } from '@zapier/common-utils';
import { Colors, Duration, Forms } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import type { ComponentType } from 'react';
import type {
  GetInputPropsReturn,
  GetRootPropsReturn,
  GetToggleButtonReturn,
} from 'downshift';

/** @jsx jsx */

const darkThemeCss = css({
  background: Colors.storm,
  color: Colors.batmobile,
});

const hideBorderCss = css({
  border: 'none',
});

const toggleBaseCss = css({
  ...Forms.field.typography,
  background: Colors.white,
  boxSizing: 'border-box',
  display: 'flex',
  cursor: 'pointer',
  alignItems: 'center',
  borderRadius: Forms.field.borderRadius,
  border: Forms.field.border,
  color: Colors.steel,
  minHeight: Forms.field.minHeight,
  padding: `0 ${Forms.field.padding}`,
  transition: `border-color ${Duration.medium} ease`,
  '&:focus': {
    outline: 'none',
    borderColor: Colors.heroine,
  },
});

const toggleHasSelectedItemCss = css({
  color: Colors.batmobile,
});

const toggleIsLoadingCss = css({
  opacity: 0.7,
  cursor: 'default',
  pointerEvents: 'none',
});

const toggleIsDisabledCss = css({
  cursor: 'default',
  pointerEvents: 'none',
  backgroundColor: Colors.storm,
});

const toggleContentCss = css({
  flex: 1,
});

const toggleIconCss = css({
  marginLeft: '12px',
});

// We can't safely assume that `Item` will always have a specific shape, because consumers that customize
// rendering might have items with additional/different properties flowing through.
type Item = *;

export type Props = {
  getInputProps: () => GetInputPropsReturn,
  getToggleButtonProps: (options: {
    'aria-label': string,
  }) => GetToggleButtonReturn,
  /** `true` to hide Toggle border */
  isBorderHidden?: boolean,
  /** Show dark theme */
  isDark?: boolean,
  /** `true` if this Toggle is disabled */
  isDisabled: boolean,
  /** `true` if the Toggle/SelectField is in a loading state */
  isLoading: boolean,
  onClick: () => void,
  /** Placeholder to render inside the select toggle when no item is selected. */
  placeholder: string,
  /** A11Y props returned by Downshift's getRootProps method */
  rootProps: GetRootPropsReturn,
  /** The currently selected item */
  selectedItem: Item,
  /**
   * Callback that will be used to generate the string label to be rendered for the currently selected item.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: Item) => string,
  /**
   * The Component to use for rendering the content inside the toggle.
   * WARNING: If you override this, you're also responsible for handling the cases where:
   * - `selectedItem` is empty
   * - `isLoading` is `true`.
   * - `isDisabled` is `true`.
   */
  SelectionComponent: ComponentType<*>,
};

/**
 * This is the default render for `Toggle` if `props.SelectionComponent` is not passed in.
 */
const DefaultSelectionComponent = (props: Props) => {
  const placeholder = props.isLoading ? 'Loading...' : props.placeholder;

  return (
    <span css={toggleContentCss}>
      {props.selectedItem
        ? props.getLabelForItem(props.selectedItem)
        : placeholder}
    </span>
  );
};

const Toggle = (props: Props) => {
  const {
    isBorderHidden,
    isDark,
    isDisabled,
    isLoading,
    onClick,
    placeholder,
    rootProps,
    SelectionComponent,
  } = props;

  const { innerRef, ...rootA11yProps } = rootProps;

  const toggleCss = css(
    toggleBaseCss,
    !!props.selectedItem && toggleHasSelectedItemCss,
    isLoading && toggleIsLoadingCss,
    isDisabled && toggleIsDisabledCss,
    isDark && darkThemeCss,
    isBorderHidden && hideBorderCss
  );

  // We need to provide aria-activedescendant to signal interaction with listbox items
  // Downshift provies this value to be applied to the input field. Since, we do not have
  // an input field in this component, we are not using the input props. This extracts
  // the needed attribute from input props and applies it to the element controlling the listbox.
  const activeDescendent = props.getInputProps()['aria-activedescendant'] || '';

  return (
    <div
      aria-activedescendant={activeDescendent}
      css={toggleCss}
      onClick={onClick}
      onKeyDown={createKeyHandler(['enter', 'space'], onClick)}
      ref={innerRef}
      role="menu"
      tabIndex={0}
      {...rootA11yProps}
      {...props.getToggleButtonProps({
        disabled: isLoading || isDisabled,
        'aria-label': placeholder,
        'aria-haspopup': 'menu',
      })}
    >
      <SelectionComponent {...props} />
      {!isDisabled && <Icon css={toggleIconCss} name="arrow-down" />}
    </div>
  );
};

Toggle.defaultProps = {
  SelectionComponent: DefaultSelectionComponent,
  getLabelForItem: (item: Item) => item.label,
};

export default Toggle;
